import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Alert } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { groupsAPI } from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';

const GroupDetail = () => {
  const [group, setGroup] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({
    userCount: 0,
    orderCount: 0,
    totalSales: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error, success]);
  
  // Confirmation modal states
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showDeleteCampaignModal, setShowDeleteCampaignModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Handle group deletion
  const handleDeleteGroup = async () => {
    try {
      setLoading(true);
      await groupsAPI.deleteGroup(id);
      setSuccess('Group deleted successfully');
      
      // Navigate back to groups list after a short delay
      setTimeout(() => {
        navigate('/admin/groups');
      }, 1500);
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(
        err.response?.data?.message || 
        'Failed to delete group. It may have active campaigns or coordinators.'
      );
      setShowDeleteGroupModal(false);
      setLoading(false);
    }
  };
  
  // Handle campaign deletion
  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    
    try {
      setLoading(true);
      // Note: This API endpoint needs to be implemented in the backend
      await groupsAPI.deleteCampaign(id, campaignToDelete._id);
      
      // Update campaigns list
      setCampaigns(campaigns.filter(c => c._id !== campaignToDelete._id));
      setSuccess('Campaign deleted successfully');
      setShowDeleteCampaignModal(false);
      setLoading(false);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError(
        err.response?.data?.message || 
        'Failed to delete campaign'
      );
      setShowDeleteCampaignModal(false);
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch group details
        const groupResponse = await groupsAPI.getGroupById(id);
        setGroup(groupResponse.data.data);
        
        // Fetch campaigns for this group
        const campaignsResponse = await groupsAPI.getGroupCampaigns(id);
        setCampaigns(campaignsResponse.data.data);
        
        // Fetch group stats
        const statsResponse = await groupsAPI.getGroupStats(id);
        setStats(statsResponse.data.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching group details:', err);
        setError('Failed to load group details');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  if (loading) {
    return <div className="loading-spinner">Loading group details...</div>;
  }
  
  if (!group) {
    return <Alert variant="warning">Group not found</Alert>;
  }
  
  return (
    <Container>
      {success && (
        <Alert 
          variant="success" 
          className="mt-3" 
          dismissible 
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert 
          variant="danger" 
          className="mt-3" 
          dismissible 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Group Details</h2>
        <Button 
          variant="secondary" 
          onClick={() => navigate('/admin/groups')}
        >
          Back to Groups
        </Button>
      </div>
      
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h3>{group.name}</h3>
            <div>
              <Link to={`/admin/groups/${id}/edit`} className="me-2">
                <Button variant="outline-primary" size="sm">Edit Group</Button>
              </Link>
              <Button 
                variant="outline-danger" 
                size="sm"
                onClick={() => setShowDeleteGroupModal(true)}
              >
                Delete Group
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Group Code:</strong> {group.code}</p>
              <p><strong>Organization:</strong> {group.organizationName}</p>
              <p><strong>Contact Info:</strong> {group.contactInfo || 'N/A'}</p>
            </Col>
            <Col md={6}>
              <p><strong>Status:</strong> {group.isActive ? 'Active' : 'Inactive'}</p>
              <p><strong>Created:</strong> {new Date(group.createdAt).toLocaleDateString()}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card className="mb-4">
        <Card.Header>
          <h3>Group Statistics</h3>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Card className="text-center p-3">
                <h4>{stats.userCount}</h4>
                <p className="text-muted mb-0">Students with orders</p>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center p-3">
                <h4>{stats.orderCount}</h4>
                <p className="text-muted mb-0">Orders</p>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center p-3">
                <h4>${stats.totalSales.toFixed(2)}</h4>
                <p className="text-muted mb-0">Total Sales</p>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Campaigns</h3>
        <Link to={`/admin/groups/${id}/campaigns/new`}>
          <Button variant="primary">Add Campaign</Button>
        </Link>
      </div>
      
      {campaigns.length > 0 ? (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Description</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => (
              <tr key={campaign._id}>
                <td>{campaign.description || 'No description'}</td>
                <td>{new Date(campaign.startDate).toLocaleDateString()}</td>
                <td>{new Date(campaign.endDate).toLocaleDateString()}</td>
                <td>
                  {campaign.isActive ? (
                    campaign.isCurrentlyActive ? (
                      <span className="text-success">Active</span>
                    ) : (
                      <span className="text-warning">
                        {new Date() < new Date(campaign.startDate) ? 'Upcoming' : 'Ended'}
                      </span>
                    )
                  ) : (
                    <span className="text-danger">Inactive</span>
                  )}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Link to={`/admin/groups/${id}/campaigns/${campaign._id}/edit`}>
                      <Button variant="outline-primary" size="sm">Edit</Button>
                    </Link>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => {
                        setCampaignToDelete(campaign);
                        setShowDeleteCampaignModal(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Alert variant="info">
          No campaigns found for this group. Create your first campaign!
        </Alert>
      )}
      
      {/* Confirmation Modals */}
      <ConfirmationModal
        show={showDeleteGroupModal}
        onHide={() => setShowDeleteGroupModal(false)}
        onConfirm={handleDeleteGroup}
        title="Delete Group"
        message={`Are you sure you want to delete the group "${group.name}"? This action cannot be undone and will also delete all associated campaigns.`}
      />
      
      <ConfirmationModal
        show={showDeleteCampaignModal}
        onHide={() => setShowDeleteCampaignModal(false)}
        onConfirm={handleDeleteCampaign}
        title="Delete Campaign"
        message={
          campaignToDelete 
            ? `Are you sure you want to delete the campaign "${campaignToDelete.description || 'Unnamed Campaign'}"? This action cannot be undone.`
            : 'Are you sure you want to delete this campaign? This action cannot be undone.'
        }
      />
    </Container>
  );
};

export default GroupDetail;
